import type { EchangeDTO, ObjetDTO, Stat } from '@whatremains/shared'

/**
 * Comparer deux pièces.
 *
 * Extrait de l'écran Inventaire quand la poupée s'est mise à comparer elle
 * aussi : deux écrans qui rendent un verdict différent sur le même couple de
 * pièces est un bug qu'on ne verrait qu'en jouant, et tard.
 */

export const ABREGES: Record<Stat, string> = {
  force: 'FOR',
  adresse: 'ADR',
  esprit: 'ESP',
  vigueur: 'VIG',
  fortune: 'FTN',
}

/** La somme des bonus, comme mesure grossière de « mieux ou moins bien ». */
export function poids(o: ObjetDTO): number {
  return Object.values(o.bonus).reduce<number>((s, v) => s + (v ?? 0), 0)
}

/** Le détail stat par stat de ce qu'on gagne et de ce qu'on perd à l'échange. */
export function Ecart({ range, porte }: { range: ObjetDTO; porte: ObjetDTO | undefined }) {
  const stats = new Set<Stat>([
    ...(Object.keys(range.bonus) as Stat[]),
    ...((porte ? Object.keys(porte.bonus) : []) as Stat[]),
  ])
  const lignes = [...stats]
    .map((s) => ({ s, d: (range.bonus[s] ?? 0) - (porte?.bonus[s] ?? 0) }))
    .filter((l) => l.d !== 0)

  if (lignes.length === 0) {
    return <span className="tenu" style={{ fontFamily: 'var(--mono)', fontSize: 10 }}>égal</span>
  }

  return (
    <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5 }}>
      {lignes.map((l) => (
        <span key={l.s} style={{ marginRight: 10, color: l.d > 0 ? 'var(--r2)' : 'var(--r6)' }}>
          {ABREGES[l.s]} {l.d > 0 ? `+${l.d}` : l.d}
        </span>
      ))}
    </span>
  )
}

/**
 * Ce que l'échange ferait, en une ligne.
 *
 * Sert partout où l'on voit une pièce qu'on ne porte pas encore : l'échoppe,
 * l'étal, le dépôt de confrérie. Le calcul vient du serveur (`EchangeDTO`), qui
 * seul sait contre quoi comparer une fois la classe et la règle des mains
 * appliquées ; ici on ne fait que le mettre en couleur.
 *
 * La ligne est toujours affichée, jamais au survol seul : sur téléphone il n'y
 * a pas de survol, et c'est précisément l'information dont on a besoin pour
 * décider d'acheter.
 */
export function Echange({ echange }: { echange: EchangeDTO }) {
  if (echange.interdit) {
    return <span className="echange interdit">tu ne peux pas tenir ça</span>
  }

  const rien = echange.ecarts.length === 0 && !echange.degats
  if (rien) {
    return (
      <span className="echange">
        {echange.remplace ? (
          <>
            au lieu de <i>{echange.remplace}</i> · <span className="tenu">rien ne change</span>
          </>
        ) : (
          <span className="tenu">emplacement libre</span>
        )}
      </span>
    )
  }

  const signe = (v: number) => (v > 0 ? `+${v}` : `${v}`)

  return (
    <span className="echange">
      {echange.remplace ? (
        <>au lieu de <i>{echange.remplace}</i> · </>
      ) : (
        <>emplacement libre · </>
      )}
      {echange.degats && (
        <span className={echange.degats.max >= 0 ? 'gain' : 'perte'}>
          DÉG {signe(echange.degats.min)}/{signe(echange.degats.max)}
        </span>
      )}
      {echange.ecarts.map((e) => (
        <span key={e.stat} className={e.valeur > 0 ? 'gain' : 'perte'}>
          {ABREGES[e.stat]} {signe(e.valeur)}
        </span>
      ))}
    </span>
  )
}
